import {
  QrCode,
  BookOpen,
  CloudLightning,
  CreditCard,
  Eye,
  FileCog,
  FileText,
  Handshake,
  LayoutDashboard,
  PlaneTakeoff,
  ServerCog,
  UserSearch,
  Users,
  Inbox,
  ShieldCheck,
  type LucideIcon,
} from 'lucide-react';
import type { PermissionKey } from '@nexa/auth';
import { NAV_PERMISSIONS, type NavId } from '@/lib/access/route-permissions';

// The console's main menu, ONE model drawn two ways: the expanded sidebar (`sidebar.tsx`,
// every console page and the phone drawer) and the icon rail (`sidebar-rail.tsx`, beside
// the full-screen «Operar la disrupción» on a desktop). Same items, same order, same
// permission filter — so the two can never disagree.
//
// Sidebar per docs/ui/operator-ui-tds.md §4.3, ordered by importance (#841,
// product owner 2026-10-19). Four sections, all permission-gated; a section header
// only renders when the operator can see at least one of its items:
//
//   OPERATION     — the day's work: the dashboard (home + every way to declare),
//                   then disruptions (the most important; cases hidden for now), then
//                   flights, passengers and reservations.
//   TOOLS         — counter tools and the provider tray (the #810 console where
//                   a person plays the hotel / transport provider in manual mode).
//   MANAGEMENT    — reports and billing: neither is configuration.
//   CONFIGURATION — what the airline SETS UP and administers: policies,
//                   contracts, manifest formats, providers, users, roles.
//
// Declaring is NOT a nav item. Express (one flight), the airport picker (several
// flights) and the file import are the dashboard's action strip — one home, three
// buttons — so the sidebar stays the map and the dashboard stays the cockpit.
// /express, /imports and /formats stay addressable by URL and by the dashboard.
//
// users-roles-permissions R5 (brief §5 C1): each item shows when the operator holds its
// module's VIEW permission — `NAV_PERMISSIONS`, the same table the route guards read, so
// a link and its route never disagree. A member with no access sees no items at all.
//
// Every item has its OWN icon: in the rail the icon is all the operator sees until the
// hint opens, so two items sharing a glyph would be a guess (Contracts used to borrow
// Policies' page icon).

// Both tables hold KEYS, never language — the menus render them with `t()` in the
// `admin` namespace. A key may carry an explicit `common:` (or `counters:`) prefix to
// reuse the shared nav vocabulary.
export interface NavItem {
  /** Stable nav id — drives the link's `data-testid` and the permission lookup. */
  id: NavId;
  labelKey: string;
  icon: LucideIcon;
}

export interface NavSection {
  /** Stable id — drives the section's `data-testid`, never shown to the operator. */
  id: string;
  /** Section heading key; also the aria-label of the section's <nav>. */
  titleKey: string;
  items: readonly NavItem[];
}

const OPERATION: readonly NavItem[] = [
  { id: 'dashboard', labelKey: 'common:nav.dashboard', icon: LayoutDashboard },
  { id: 'disruptions', labelKey: 'sidebar.nav.disruptions', icon: CloudLightning },
  // «Casos» hidden from the menu for now (PO 2026-10-27: «ocultá la opción de Casos por el
  // momento»). /cases and /cases/:caseUrn stay addressable by URL and by the links that
  // lead there; put `{ id: 'cases', labelKey: 'common:nav.cases', icon: ListChecks }`
  // back here to show it again.
  { id: 'flights', labelKey: 'sidebar.nav.flights', icon: PlaneTakeoff },
  { id: 'passengers', labelKey: 'sidebar.nav.passengers', icon: UserSearch },
  { id: 'reservations', labelKey: 'sidebar.nav.reservations', icon: BookOpen },
];

const TOOLS: readonly NavItem[] = [
  { id: 'countersQr', labelKey: 'counters:nav.counterQr', icon: QrCode },
  { id: 'providerSim', labelKey: 'sidebar.nav.providerSim', icon: Inbox },
];

const MANAGEMENT: readonly NavItem[] = [
  { id: 'reports', labelKey: 'common:nav.reports', icon: Eye },
  { id: 'billing', labelKey: 'common:nav.billing', icon: CreditCard },
];

const CONFIGURATION: readonly NavItem[] = [
  { id: 'policies', labelKey: 'common:nav.policies', icon: FileText },
  { id: 'contracts', labelKey: 'common:nav.contracts', icon: Handshake },
  { id: 'formats', labelKey: 'sidebar.nav.formats', icon: FileCog },
  { id: 'providers', labelKey: 'sidebar.nav.providers', icon: ServerCog },
  { id: 'users', labelKey: 'sidebar.nav.users', icon: Users },
  // Screen registered by R6 (`/roles`); the item lands with the access foundation.
  { id: 'roles', labelKey: 'sidebar.nav.roles', icon: ShieldCheck },
];

export const NAV_SECTIONS: readonly NavSection[] = [
  { id: 'operation', titleKey: 'sidebar.section.operation', items: OPERATION },
  { id: 'tools', titleKey: 'sidebar.section.tools', items: TOOLS },
  { id: 'management', titleKey: 'sidebar.section.management', items: MANAGEMENT },
  { id: 'configuration', titleKey: 'sidebar.section.configuration', items: CONFIGURATION },
];

/** The sections an operator holding `can` sees — empty sections dropped. */
export function visibleSections(
  can: (permission: PermissionKey) => boolean,
): readonly NavSection[] {
  return NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter((item) => can(NAV_PERMISSIONS[item.id])),
  })).filter((section) => section.items.length > 0);
}
